import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";

import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";

export default function EmploymentCreate() {
  const nav = useNavigate();

  const DEFAULT_TEMPLATE = `
  <h2>근무내용</h2>
  <ul>
    <li>예) 내용을 입력해 주세요</li>
  </ul>

  <br>

  <h2>자격요건</h2>
  <ul>
    <li>예) 내용을 입력해 주세요</li>
  </ul>

  <br>

  <h2>우대사항</h2>
  <ul>
    <li>예) 내용을 입력해 주세요</li>
  </ul>

  <br>

  <h2>근무조건</h2>
  <ul>
    <li>예) 근무시간</li>
    <li>예) 급여</li>
  </ul>
`;

  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [locationText, setLocationText] = useState(""); // 근무지 텍스트
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState(DEFAULT_TEMPLATE); // ReactQuill

  const [deadline, setDeadline] = useState(""); // yyyy-mm-dd
  const [detailUrl, setDetailUrl] = useState(""); // 선택: 외부 링크
  const [isActive, setIsActive] = useState(true); // 선택: 공개 여부

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const validate = () => {
    if (!title.trim()) return "제목을 입력해 주세요.";
    if (!company.trim()) return "회사명을 입력해 주세요.";
    if (!locationText.trim()) return "근무지(텍스트)를 입력해 주세요.";
    if (!summary.trim()) return "요약을 입력해 주세요.";

    // Quill은 비어있어도 <p><br></p> 등 HTML이 들어올 수 있음 → 태그 제거 후 체크
    const plain = content.replace(/<[^>]+>/g, "").trim();
    if (!plain) return "내용을 입력해 주세요.";

    // deadline 선택 필수로 할지 애매하면 여기서 빼면 됨
    if (!deadline) return "마감일을 선택해 주세요.";

    // URL 형식(선택 입력)
    if (detailUrl.trim()) {
      try {
        // eslint-disable-next-line no-new
        new URL(detailUrl.trim());
      } catch {
        return "상세 링크(URL) 형식이 올바르지 않습니다.";
      }
    }
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      // 🔹 XSS 방지
      const sanitizedContent = DOMPurify.sanitize(content);

      // 🔹 백엔드로 보낼 payload
      const payload = {
        title: title.trim(),
        company: company.trim(),
        locationText: locationText.trim(),
        summary: summary.trim(),
        content: sanitizedContent,
        deadline, // yyyy-mm-dd (백엔드에서 LocalDate/DateTime 처리)
        detailUrl: detailUrl.trim() || null,
        isActive,
      };

      // ✅ 엔드포인트는 프로젝트에 맞게 변경 가능
      // 예: /api/admin/employments, /api/employments 등
      const res = await api.post("/api/employments", payload, {
        headers: { "Content-Type": "application/json" },
      });

      const resultCode = res.data?.resultCode;

      if (
        resultCode === "201" ||
        resultCode === 201 ||
        resultCode === "200" ||
        resultCode === 200
      ) {
        setMessage("채용공고가 등록되었습니다.");
        nav("/admin/employment/list");
      } else {
        setError(
          res.data?.resultMessage ||
            "채용공고 등록에 실패했습니다. 다시 시도해 주세요."
        );
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.resultMessage ||
          "채용공고 등록 중 오류가 발생했습니다."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "link",
    "image",
  ];

  return (
    <AdminLayout>
      {/* 헤더 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
          채용공고 등록
        </h1>

        <button
          type="button"
          onClick={() => nav("/admin/employment/list")}
          className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          목록으로
        </button>
      </div>

      <form
        onSubmit={onSubmit}
        className="space-y-6 rounded-2xl border bg-white p-6 shadow-sm"
      >
        {/* 제목 */}
        <Field label="제목" required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="제목을 입력해 주세요."
          />
        </Field>

        {/* 회사명 */}
        <Field label="회사명" required>
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="회사명을 입력해 주세요."
          />
        </Field>

        {/* 근무지 텍스트 */}
        <Field label="근무지(텍스트)" required>
          <input
            value={locationText}
            onChange={(e) => setLocationText(e.target.value)}
            className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="예) 말레이시아(훈련센터) / 부산 / 재택 등"
          />
        </Field>

        {/* 요약 */}
        <Field label="요약" required>
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            className="w-full resize-none rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="리스트에 노출될 짧은 요약을 입력해 주세요."
          />
          <p className="mt-1 text-xs text-slate-500">
            리스트 카드에서 1~2줄로 보이므로 핵심만 적는 게 좋아요.
          </p>
        </Field>

        {/* 내용 (ReactQuill) */}
        <Field label="내용" required>
          <div className="rounded-xl border">
            <ReactQuill
              theme="snow"
              value={content}
              onChange={setContent}
              modules={quillModules}
              formats={quillFormats}
              placeholder="채용공고 내용을 입력해 주세요."
              className="quill-editor"
            />
          </div>
          <p className="mt-1 text-xs text-slate-500">
            간단한 서식, 링크, 이미지 등을 사용할 수 있습니다.
          </p>
        </Field>

        {/* 마감일 */}
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="마감일" required>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            />
          </Field>

          <Field label="상세 링크(URL)" required={false}>
            <input
              value={detailUrl}
              onChange={(e) => setDetailUrl(e.target.value)}
              className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="예) https://example.com/job/123 (선택)"
            />
          </Field>
        </div>

        {/* 공개 여부 */}
        <Field label="공개 여부" required={false}>
          <div className="flex items-center gap-3">
            <input
              id="isActive"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="isActive" className="text-sm text-slate-700">
              공개(사용자에게 노출)
            </label>
          </div>
        </Field>

        {/* 에러/메시지 */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            {message}
          </div>
        )}

        {/* 버튼 */}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => nav("/admin/employment/list")}
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? "저장 중..." : "등록"}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="grid gap-1 text-sm">
      <div className="font-medium text-slate-800">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </div>
      {children}
    </div>
  );
}
